import { useSelector } from 'react-redux'
import { useState, useMemo } from 'react'
import { useRouter } from 'next/router'
import { Placeholder } from 'rsuite'
import qs from 'qs'
import moment from 'moment'
import useTranslation from '@/hooks/useTranslation'
import ExploreButton from './exploreButton'
import ExploreDropDown from './exploreDropDown'
import useModelAlias from '@/hooks/useModelAlias'
import { configSelectors } from '@/store/configs'
import { searchSelectors } from '@/store/search'
import { i18nL10nSelectors } from '@/store/i18nL10n'
import Image from 'next/image'

const SelectedCriteriaPane = ({ isOverview, lineOffYears, selectedLineOffYear, onSelectLineOffYear }) => {
  // <Redux>
  const modelLangMarketingNames = useSelector(configSelectors.selectConfigByKey('modelLangMarketingNames'))
  const languageNames = useSelector(configSelectors.selectConfigByKey('languages'))
  const { generation, modelType } = useSelector(searchSelectors.selectPane)
  const locale = useSelector(i18nL10nSelectors.selectLocale)
  const {
    filterBy: { brand, model, ngtdModelId, katashiki, productionDate, language, dateFrom, dateTo }
  } = useSelector(searchSelectors.selectQuery)
  // </Redux>
  const t = useTranslation()
  const router = useRouter()
  const getModelAlias = useModelAlias(modelLangMarketingNames, locale)
  const languagesPageQuery = { brand, model, modelType }
  const shouldShowYearSelector = useMemo(() => {
    if (isOverview && katashiki) {
      return false
    }
    return lineOffYears && selectedLineOffYear
  }, [isOverview, katashiki, lineOffYears, selectedLineOffYear])

  const [imageError, setImageError] = useState(false)

  const handleImageError = () => {
    setImageError(true)
  }

  if (generation) {
    languagesPageQuery.generationId = generation.id
    languagesPageQuery.ngtdModelId = ngtdModelId
  }

  if (katashiki) languagesPageQuery.katashiki = katashiki
  if (productionDate) languagesPageQuery.productionDate = productionDate
  if (dateFrom) languagesPageQuery.dateFrom = dateFrom
  if (dateTo) languagesPageQuery.dateTo = dateTo

  return (
    <div
      className={`mt-10 ${!isOverview && 'mb-10'} selected-criteria-pane ${isOverview && 'flex-l'}`}
      style={isOverview && { alignItems: 'flex-start' }}
    >
      <div className={`model-container ${isOverview ? 'flex-column-reverse mr-20' : 'flex-column'}`}>
        {model && !modelType && <div className="mb-10 model-name">{getModelAlias(model, 'model')}</div>}
        {modelType && (
          <div className="model-type">
            {!generation && <div className="mb-10 model-name">{getModelAlias(modelType, 'modelType')}</div>}
            {generation && (
              <>
                {!isOverview && (
                  <div className="image">
                    {generation.image && !imageError ? (
                      <Image
                        src={`/api/images/generations/${generation?.image?.uniqueImageId}`}
                        alt={`${generation.yearFrom}-${generation.yearTo || 'current'} generation`}
                        className="generation-image"
                        width={200}
                        height={130}
                        onError={handleImageError}
                      />
                    ) : (
                      <Placeholder.Graph active height={130} width={200} />
                    )}
                  </div>
                )}
                <div className="mt-10 mb-10 model-name">
                  {getModelAlias(modelType, 'modelType')}
                  <div className="generation">
                    {generation.yearFrom} -{' '}
                    {generation.yearTo ||
                      (parseInt(generation.yearFrom) > moment().year() ? generation.yearFrom : moment().year())}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
        <ExploreButton size="sm" appearance="link" onClick={() => router.push('/')}>
          {t('finding_vehicle_change_vehicle')}
        </ExploreButton>
      </div>
      {language && (
        <div className={`${isOverview ? 'flex-column-reverse mb-0' : 'flex-column mt-10'} language-container`}>
          <div className="mb-10 language">{t(languageNames[language] || language)}</div>
          <ExploreButton
            size="sm"
            appearance="link"
            onClick={() => router.push(`/languages?${qs.stringify(languagesPageQuery)}`)}
          >
            {t('finding_vehicle_change_language')}
          </ExploreButton>
        </div>
      )}
      {shouldShowYearSelector && (
        <div className={`${isOverview ? 'flex-column-reverse mb-0' : 'flex-column mt-10'} modelyear-container`}>
          <ExploreDropDown
            data={lineOffYears.map(year => ({ label: year, value: year }))}
            value={selectedLineOffYear}
            onChange={onSelectLineOffYear}
            searchable={false}
            cleanable={false}
            label={t('finding_vehicle_change_modelyear', 'Model year')}
            style={{ width: '100%' }}
          />
        </div>
      )}
    </div>
  )
}

export default SelectedCriteriaPane
